import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n';
import TranslatedText from './TranslatedText';
import axios from 'axios';
import { Award, Leaf, Users, Shield, Share2, X, Download, CheckCircle, Twitter, Facebook, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Definizione livelli badge
const BADGE_LEVELS = {
  environmental: [
    { min: 0, max: 1, name: 'Iniziato', color: '#94A3B8', icon: '🌱' },
    { min: 1, max: 5, name: 'Guardiano', color: '#22C55E', icon: '🌿' },
    { min: 5, max: 15, name: 'Protettore', color: '#10B981', icon: '🌳' },
    { min: 15, max: 50, name: 'Campione', color: '#059669', icon: '🏆' },
    { min: 50, max: Infinity, name: 'Leggenda', color: '#047857', icon: '⭐' }
  ],
  social: [
    { min: 0, max: 2, name: 'Volontario', color: '#94A3B8', icon: '👤' },
    { min: 2, max: 10, name: 'Attivista', color: '#3B82F6', icon: '💪' },
    { min: 10, max: 30, name: 'Leader', color: '#2563EB', icon: '🎯' },
    { min: 30, max: 100, name: 'Ispiratore', color: '#1D4ED8', icon: '✨' },
    { min: 100, max: Infinity, name: 'Eroe', color: '#1E40AF', icon: '🦸' }
  ],
  collections: [
    { min: 0, max: 3, name: 'Novizio', color: '#94A3B8', icon: '📦' },
    { min: 3, max: 10, name: 'Raccoglitore', color: '#06B6D4', icon: '🗑️' },
    { min: 10, max: 25, name: 'Veterano', color: '#0891B2', icon: '🏅' },
    { min: 25, max: 50, name: 'Esperto', color: '#0E7490', icon: '🎖️' },
    { min: 50, max: Infinity, name: 'Maestro', color: '#155E75', icon: '👑' }
  ]
};

const getBadgeLevel = (type, value) => {
  const levels = BADGE_LEVELS[type];
  for (const level of levels) {
    if (value >= level.min && value < level.max) {
      return level;
    }
  }
  return levels[levels.length - 1];
};

const ESGBadges = ({ volunteerName }) => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const [stats, setStats] = useState({
    totalKg: 0,
    totalCO2: 0,
    totalHours: 0,
    totalCollections: 0
  });
  const [loading, setLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/collections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const collections = response.data;
      const totalKg = collections.reduce((sum, c) => sum + (c.kg_collected || 0), 0);
      const totalHours = collections.reduce((sum, c) => sum + (c.hours_spent || 0), 0);
      
      setStats({
        totalKg: totalKg,
        totalCO2: parseFloat((totalKg * 0.15).toFixed(2)),
        totalHours: totalHours,
        totalCollections: collections.length
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const envBadge = getBadgeLevel('environmental', stats.totalCO2);
  const socialBadge = getBadgeLevel('social', stats.totalHours);
  const collectionsBadge = getBadgeLevel('collections', stats.totalCollections);

  const handleShareBadge = (badge, type) => {
    setSelectedBadge({ ...badge, type, stats });
    setShowShareModal(true);
  };

  const shareOnSocial = (platform) => {
    if (!selectedBadge) return;

    const text = `🌊 Ho ottenuto il badge "${selectedBadge.icon} ${selectedBadge.name}" su Respiro Blu!\n\n` +
      `📊 Il mio impatto ESG:\n` +
      `🌿 ${stats.totalCO2} kg CO₂ evitata\n` +
      `⏱️ ${stats.totalHours} ore di volontariato\n` +
      `📦 ${stats.totalCollections} raccolte completate\n\n` +
      `Unisciti a noi per salvare il mare! 🐢\n` +
      `#RespiroBlu #MarineLitter #ESG #Volontariato`;

    const url = 'https://respiro-dashboard.emergent.host/';
    
    let shareUrl = '';
    
    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(text + '\n' + url)}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(text + '\n' + url);
        toast.success('Testo copiato negli appunti!');
        setShowShareModal(false);
        return;
      default:
        return;
    }

    window.open(shareUrl, '_blank', 'width=600,height=400');
    setShowShareModal(false);
  };

  if (loading) {
    return (
      <div className="bg-slate-900/60 backdrop-blur-xl border border-violet-500/30 rounded-2xl p-6">
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-slate-900/60 backdrop-blur-xl border border-violet-500/30 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Award className="w-6 h-6 text-violet-400" />
            <h3 className="text-lg font-bold text-violet-400" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
              CERTIFICAZIONI ESG
            </h3>
          </div>
          <span className="text-xs text-slate-500">Condividi il tuo impatto</span>
        </div>

        {/* Riepilogo Impatto */}
        <div className="grid grid-cols-3 gap-3 mb-6 p-4 bg-slate-800/50 rounded-xl">
          <div className="text-center">
            <p className="text-2xl font-bold text-emerald-400">{stats.totalCO2}</p>
            <p className="text-[10px] text-slate-500 uppercase">kg CO₂ evitata</p>
          </div>
          <div className="text-center border-x border-slate-700">
            <p className="text-2xl font-bold text-blue-400">{stats.totalHours}</p>
            <p className="text-[10px] text-slate-500 uppercase">ore volontariato</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-cyan-400">{stats.totalCollections}</p>
            <p className="text-[10px] text-slate-500 uppercase">raccolte</p>
          </div>
        </div>

        {/* Badge Cards */}
        <div className="space-y-4">
          {/* Environmental Badge */}
          <div 
            className="relative p-4 rounded-xl border-2 transition-all hover:scale-[1.02] cursor-pointer"
            style={{ 
              backgroundColor: `${envBadge.color}10`,
              borderColor: `${envBadge.color}50`
            }}
            onClick={() => handleShareBadge(envBadge, 'environmental')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div 
                  className="w-14 h-14 rounded-full flex items-center justify-center text-2xl"
                  style={{ backgroundColor: `${envBadge.color}30` }}
                >
                  {envBadge.icon}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">E</span>
                    <span className="text-emerald-400 text-xs font-medium">Environmental</span>
                  </div>
                  <p className="text-white font-bold text-lg">{envBadge.name}</p>
                  <p className="text-slate-400 text-xs">{stats.totalCO2} kg CO₂ evitata</p>
                </div>
              </div>
              <button
                className="p-2 rounded-full hover:bg-white/10 transition-all"
                onClick={(e) => {
                  e.stopPropagation();
                  handleShareBadge(envBadge, 'environmental');
                }}
              >
                <Share2 className="w-5 h-5 text-emerald-400" />
              </button>
            </div>
          </div>

          {/* Social Badge */}
          <div 
            className="relative p-4 rounded-xl border-2 transition-all hover:scale-[1.02] cursor-pointer"
            style={{ 
              backgroundColor: `${socialBadge.color}10`,
              borderColor: `${socialBadge.color}50`
            }}
            onClick={() => handleShareBadge(socialBadge, 'social')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div 
                  className="w-14 h-14 rounded-full flex items-center justify-center text-2xl"
                  style={{ backgroundColor: `${socialBadge.color}30` }}
                >
                  {socialBadge.icon}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="bg-blue-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">S</span>
                    <span className="text-blue-400 text-xs font-medium">Social</span>
                  </div>
                  <p className="text-white font-bold text-lg">{socialBadge.name}</p>
                  <p className="text-slate-400 text-xs">{stats.totalHours} ore di volontariato</p>
                </div>
              </div>
              <button
                className="p-2 rounded-full hover:bg-white/10 transition-all"
                onClick={(e) => {
                  e.stopPropagation();
                  handleShareBadge(socialBadge, 'social');
                }}
              >
                <Share2 className="w-5 h-5 text-blue-400" />
              </button>
            </div>
          </div>

          {/* Collections Badge */}
          <div 
            className="relative p-4 rounded-xl border-2 transition-all hover:scale-[1.02] cursor-pointer"
            style={{ 
              backgroundColor: `${collectionsBadge.color}10`,
              borderColor: `${collectionsBadge.color}50`
            }}
            onClick={() => handleShareBadge(collectionsBadge, 'collections')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div 
                  className="w-14 h-14 rounded-full flex items-center justify-center text-2xl"
                  style={{ backgroundColor: `${collectionsBadge.color}30` }}
                >
                  {collectionsBadge.icon}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">G</span>
                    <span className="text-amber-400 text-xs font-medium">Governance</span>
                  </div>
                  <p className="text-white font-bold text-lg">{collectionsBadge.name}</p>
                  <p className="text-slate-400 text-xs">{stats.totalCollections} raccolte certificate</p>
                </div>
              </div>
              <button
                className="p-2 rounded-full hover:bg-white/10 transition-all"
                onClick={(e) => {
                  e.stopPropagation();
                  handleShareBadge(collectionsBadge, 'collections');
                }}
              >
                <Share2 className="w-5 h-5 text-amber-400" />
              </button>
            </div>
          </div>
        </div>

        {/* Certificato Verificato */}
        <div className="mt-6 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
          <p className="text-emerald-400 text-xs">
            Certificazione verificata tramite GPS e documentazione fotografica
          </p>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && selectedBadge && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setShowShareModal(false)}
        >
          <div 
            className="bg-slate-900 border border-violet-500/50 rounded-2xl p-6 max-w-md w-full"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-white">Condividi il tuo Badge</h3>
              <button 
                onClick={() => setShowShareModal(false)}
                className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>

            {/* Preview Badge */}
            <div 
              className="p-6 rounded-xl mb-6 text-center"
              style={{ backgroundColor: `${selectedBadge.color}20` }}
            >
              <div className="text-5xl mb-3">{selectedBadge.icon}</div>
              <p className="text-white font-bold text-xl mb-1">{selectedBadge.name}</p>
              <p className="text-slate-400 text-sm mb-4">
                {volunteerName || 'Volontario Respiro Blu'}
              </p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-emerald-400 font-bold">{stats.totalCO2}</p>
                  <p className="text-[10px] text-slate-500">kg CO₂</p>
                </div>
                <div>
                  <p className="text-blue-400 font-bold">{stats.totalHours}</p>
                  <p className="text-[10px] text-slate-500">ore</p>
                </div>
                <div>
                  <p className="text-cyan-400 font-bold">{stats.totalCollections}</p>
                  <p className="text-[10px] text-slate-500">raccolte</p>
                </div>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => shareOnSocial('whatsapp')}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-all"
              >
                <MessageCircle className="w-5 h-5" />
                <span className="font-medium">WhatsApp</span>
              </button>
              <button
                onClick={() => shareOnSocial('facebook')}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all"
              >
                <Facebook className="w-5 h-5" />
                <span className="font-medium">Facebook</span>
              </button>
              <button
                onClick={() => shareOnSocial('twitter')}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-sky-500 hover:bg-sky-600 text-white rounded-xl transition-all"
              >
                <Twitter className="w-5 h-5" />
                <span className="font-medium">Twitter/X</span>
              </button>
              <button
                onClick={() => shareOnSocial('copy')}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-all"
              >
                <Download className="w-5 h-5" />
                <span className="font-medium">Copia</span>
              </button>
            </div>

            <p className="text-center text-slate-500 text-xs mt-4">
              Condividi il tuo impatto ambientale sui social
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ESGBadges;
